import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import { useAuthStore } from './stores/auth'
import './styles/main.css'

// Routes
const routes = [
    {
        path: '/',
        name: 'landing',
        component: () => import('./pages/LandingPage.vue')
    },
    {
        path: '/hunt',
        name: 'hunt',
        component: () => import('./pages/HuntingPage.vue'),
        meta: { requiresAuth: true }
    },
    {
        path: '/potential',
        name: 'potential',
        component: () => import('./pages/PotentialTokensPage.vue'),
        meta: { requiresAuth: true }
    },
    {
        path: '/tracked_wallets',
        name: 'tracked_wallets',
        component: () => import('./pages/TrackedWalletsPage.vue'),
        meta: { requiresAuth: true }
    },
    {
        path: '/custom_track',
        name: 'custom_track',
        component: () => import('./pages/CustomTrackPage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/admin_track_wallet',
        name: 'admin_track_wallet',
        component: () => import('./pages/AdminTrackedWalletsPage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/admin_track_wallet/list',
        name: 'admin_tracked_wallets_list',
        component: () => import('./pages/AdminTrackedWalletsList.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
        path: '/my_wallets',
        name: 'my_wallets',
        component: () => import('./pages/MyWalletsPage.vue'),
        meta: { requiresAuth: true }
    },
    {
        path: '/wallet-analyzer',
        name: 'wallet_analyzer',
        component: () => import('./pages/WalletAnalyzerPage.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes
})

// Create Pinia instance first so we can use stores
const pinia = createPinia()

// Auth guard - optimized to minimize redundant API calls
router.beforeEach(async (to, _from, next) => {
    // Skip auth check for public routes
    if (!to.meta.requiresAuth) {
        next()
        return
    }

    const authStore = useAuthStore(pinia)

    // Dev bypass: auto-authenticate with mock admin user
    if (authStore.isDevBypass) {
        if (!authStore.initialized) {
            authStore.initDevUser()
        }
        next()
        return
    }

    // Handle magic link redirect: wait for Supabase to process the URL tokens
    const hash = window.location.hash
    if (hash && hash.includes('access_token')) {
        // Don't clear the hash - Supabase needs it to exchange tokens for a session
        // Wait for Supabase to process the auth callback
        await authStore.handleAuthCallback()

        // Now clean up the URL
        window.history.replaceState(null, '', window.location.pathname)

        if (authStore.isAuthenticated) {
            next()
            return
        }
        // If still not authenticated, fall through to redirect
    }

    // Check if we already have a valid session in the auth store
    if (authStore.initialized && authStore.isAuthenticated) {
        // Already authenticated, check admin if needed
        if (to.meta.requiresAdmin && !authStore.isAdmin) {
            if (import.meta.env.DEV) console.warn('Non-admin user attempted to access admin route:', to.path)
            next({ name: 'hunt' })
            return
        }
        next()
        return
    }

    // Not initialized or not authenticated - do a single session check
    const isValid = await authStore.checkSession()

    if (!isValid) {
        next({ name: 'landing' })
        return
    }

    // For admin routes, verify admin status
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
        if (import.meta.env.DEV) console.warn('Non-admin user attempted to access admin route:', to.path)
        next({ name: 'hunt' })
        return
    }

    next()
})

const app = createApp(App)
app.use(pinia)
app.use(router)

// Setup auth state change listener after app is created
const authStore = useAuthStore(pinia)
authStore.setupAuthListener()

app.mount('#app')
